export type MarketingTrackingConsentState =
  | "granted"
  | "denied"
  | "unknown";

type TrackingUrlEntry = {
  url: string;
  title?: string;
};

type MarketingTrackingPayload = {
  landing_page: string;
  referrer_url: string;
  exit_page?: string;
  first_landing_page: string | null;
  first_referrer_url: string | null;
  navigation_path: TrackingUrlEntry[];
  referrer_chain: string[];
};

export const stripUrlQueryAndFragment = (url: string): string => {
  try {
    const sanitizedUrl = new URL(url);
    sanitizedUrl.search = "";
    sanitizedUrl.hash = "";
    sanitizedUrl.username = "";
    sanitizedUrl.password = "";
    return sanitizedUrl.toString();
  } catch {
    return "";
  }
};

export const redactMarketingTrackingPayload = <
  TrackingPayload extends MarketingTrackingPayload,
>(
  trackingData: TrackingPayload,
  consentState: MarketingTrackingConsentState,
  marketingAttributionFields: readonly string[],
): TrackingPayload => {
  if (consentState === "granted") {
    return trackingData;
  }

  const redactedData = {
    ...trackingData,
    first_landing_page: null,
    first_referrer_url: null,
    landing_page: stripUrlQueryAndFragment(trackingData.landing_page),
    referrer_url: stripUrlQueryAndFragment(trackingData.referrer_url),
    exit_page: stripUrlQueryAndFragment(trackingData.exit_page ?? ""),
    navigation_path: trackingData.navigation_path.map((entry) => ({
      ...entry,
      url: stripUrlQueryAndFragment(entry.url),
      ...(entry.title === undefined ? {} : { title: "" }),
    })),
    referrer_chain: trackingData.referrer_chain
      .map(stripUrlQueryAndFragment)
      .filter(Boolean),
  } as TrackingPayload;
  const redactedRecord = redactedData as unknown as Record<string, unknown>;

  marketingAttributionFields.forEach((fieldName) => {
    redactedRecord[fieldName] = null;
  });

  return redactedData;
};
